import type { MouseEventHandler } from 'react';
import React, { useEffect } from 'react';
import { Box, ButtonV2, DialogV2, Flex, Stack, TextV1 } from '@withjoy/joykit';
import { StyledAppButton, StyledDialog, StyledFlex, StyledMobileDialog, StyledMobileDialogContent, styles } from './ShopCurrencyWarningDialog.styles';
import { ReactComponent as CtaIconOne } from '@assets/icons/icon-wand.svg';
import { ReactComponent as CtaIconTwo } from '@assets/icons/icon-mixer-link.svg';
import { useShopCurrencyWarningDialogController } from './ShopCurrencyWarningDialog.controller';
import { useShippingAddressTelemetry } from '@apps/registry/common/components/ShippingAddressAndCurrencyDialogsProvider/ShippingAddress.telemetry';

const DIALOG_ID = 'currency-picker-warning';

export interface ShopCurrencyWarningDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddGift?: () => void;
  showInstallBrowserButton: MouseEventHandler | undefined;
  onAddAnyway: () => void;
  eventId: string;
}

export const ShopCurrencyWarningDialog: React.FC<ShopCurrencyWarningDialogProps> = props => {
  const { isOpen, onClose, eventId } = props;
  const { translations, isMobile, getAppStoreLink, onShowInstallBrowserButton, onShowAddGiftDialog, handleOnAddAnyway } = useShopCurrencyWarningDialogController(props);
  const { shopCurrencyWarningDialogOpened } = useShippingAddressTelemetry({ addToContext: { eventId } });

  useEffect(() => {
    if (isOpen) {
      shopCurrencyWarningDialogOpened();
    }
  }, [shopCurrencyWarningDialogOpened, isOpen]);

  return isMobile ? (
    <StyledMobileDialog
      id={DIALOG_ID}
      isOpen={isOpen}
      onClose={onClose}
    >
      <StyledMobileDialogContent>
        <Box padding="24px">
          <TextV1
            maxWidth={400}
            margin="0 auto"
            textAlign="center"
            marginBottom={3}
            component="h1"
            variant="hed2"
          >
            {translations.title}
          </TextV1>
          <TextV1
            component="p"
            textAlign="center"
            variant="body1"
          >
            {translations.mobileSubtitle}
          </TextV1>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          padding="24px"
        >
          <a href={getAppStoreLink()}>
            <StyledAppButton
              marginBottom={5}
              overrides={styles.registryPrimaryButton}
            >
              {translations.mobileButton}
            </StyledAppButton>
          </a>
          <ButtonV2
            variant="link"
            overrides={styles.registryLinkButton}
            onClick={onClose}
          >
            {translations.dismiss}
          </ButtonV2>
        </Box>
      </StyledMobileDialogContent>
    </StyledMobileDialog>
  ) : (
    <StyledDialog
      id={DIALOG_ID}
      isOpen={isOpen}
      onClose={onClose}
    >
      <DialogV2.Body>
        <Box
          maxWidth={504}
          margin="0 auto"
        >
          <TextV1
            maxWidth={400}
            margin="0 auto"
            textAlign="center"
            marginBottom={3}
            component="h1"
            variant="hed4"
          >
            {translations.title}
          </TextV1>
          <TextV1
            component="p"
            textAlign="center"
            variant="body1"
          >
            {translations.subtitle}
          </TextV1>
        </Box>
        <Box
          marginBottom={32}
          marginTop={6}
          display="flex"
          flexDirection="column"
        >
          <StyledFlex
            paddingX={7}
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            <Stack
              spacing={5}
              maxWidth={240}
              marginRight={[3, null, 8]}
              marginBottom={[8, null, 0]}
              alignItems="center"
            >
              <Flex
                height={72}
                flexDirection="column"
                justifyContent="flex-end"
              >
                <CtaIconOne />
              </Flex>
              <TextV1
                variant="hed1"
                component="p"
                maxWidth={208}
                textAlign="center"
              >
                {translations.ctaOne}
              </TextV1>
              <ButtonV2
                overrides={styles.registryPrimaryButton}
                onClick={onShowInstallBrowserButton}
                marginRight={8}
              >
                {translations.ctaOneButton}
              </ButtonV2>
            </Stack>
            <Stack
              spacing={5}
              maxWidth={240}
              alignItems="center"
            >
              <Flex
                height={72}
                flexDirection="column"
                justifyContent="flex-end"
              >
                <CtaIconTwo />
              </Flex>
              <TextV1
                variant="hed1"
                component="p"
                maxWidth={208}
                textAlign="center"
              >
                {translations.ctaTwo}
              </TextV1>
              <ButtonV2
                variant="outline"
                overrides={styles.registryOutlineButton}
                onClick={onShowAddGiftDialog}
              >
                {translations.ctaTwoButton}
              </ButtonV2>
            </Stack>
          </StyledFlex>
          <ButtonV2
            overrides={styles.registryLinkButton}
            marginTop={24}
            variant="link"
            onClick={handleOnAddAnyway}
          >
            {translations.addAnyway}
          </ButtonV2>
        </Box>
      </DialogV2.Body>
    </StyledDialog>
  );
};
